/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import { getCssVariableValue } from 'frontend-utils/utils/css-variable';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import MarketTotalRevenueContributionBreakdownTooltip from 'lumen/components/market/total-revenue/contribution-breakdown/tooltip';
import type {
  TotalRevenueBreakdownItem,
  TotalRevenueBreakdownTrendItem,
} from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';
import { SIGNIFICANT_RELATIVE_CHANGE_THRESHOLD } from 'lumen/utils/constants/total-revenue-constants';

const ZONE_COLORS = {
  POSITIVE: getCssVariableValue(
    '--prism-color-chart-status-figure-success-normal',
  ),
  NEGATIVE: getCssVariableValue(
    '--prism-color-chart-status-figure-critical-normal',
  ),
  SAME: getCssVariableValue('--prism-color-chart-status-figure-warning-normal'),
};

interface ExtendedAxis extends Highcharts.Axis {
  dataMax: number;
  dataMin: number;
}

interface MarketTotalRevenueContributionBreakdownTrendChartSignature {
  Args: {
    department: TotalRevenueBreakdownItem;
    isSelected: boolean | undefined;
    selectedReferenceType: string;
    trendItems: TotalRevenueBreakdownTrendItem[];
  };
}

export default class MarketTotalRevenueContributionBreakdownTrendChart extends Component<MarketTotalRevenueContributionBreakdownTrendChartSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked hoveredPoint?: TotalRevenueBreakdownTrendItem;

  declare tooltipElement: HTMLElement;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const zoneColor = this.zoneColorToUse;

    const currentPeriodSeries = {
      id: 'current-period',
      type: 'area',
      lineWidth: 3,
      data: [] as { x: number; y: number | null }[],
      zones: [
        {
          color: zoneColor,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${zoneColor}20`],
              [1, `${zoneColor}10`],
            ],
          },
        },
      ],
    };

    const referenceSeries = {
      id: 'reference',
      type: 'line',
      lineWidth: 2,
      data: [] as { x: number; y: number | null }[],
      color: getCssVariableValue('--prism-color-general-neutral-400'),
    };

    this.args.trendItems.forEach((trendItem) => {
      const xValue = this.getXValueForDate(trendItem.date);

      currentPeriodSeries.data.push({
        x: xValue,
        y: trendItem.valueOrPAR,
      });

      const referenceValue = trendItem.referenceValues.find(
        (reference) =>
          reference.referenceType === this.args.selectedReferenceType,
      );

      if (referenceValue) {
        referenceSeries.data.push({
          x: xValue,
          y: referenceValue.valueOrPAR,
        });
      }
    });

    return [referenceSeries, currentPeriodSeries];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    if (this.args.isSelected) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;

      return {
        chart: {
          height: 256,
          zoomType: '' as Highcharts.OptionsZoomTypeValue,
        },
        tooltip: {
          shared: true,
          crosshairs: true,
          borderWidth: 0,
          shadow: false,
          padding: 0,
          outside: true,
          useHTML: true,
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            begin();
            self.hoveredPoint = self.dataByXValue[this.x];
            end();

            return self.tooltipElement.innerHTML;
          },
        },
        legend: {
          enabled: false,
        },
        xAxis: {
          type: 'datetime' as const,
        },
        yAxis: {
          title: {
            text: null,
          },
        },
      };
    }

    return {
      chart: {
        height: 160,
        spacing: [0, 0, 0, 0],
        zoomType: '' as Highcharts.OptionsZoomTypeValue,
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
        area: {
          enableMouseTracking: false,
        },
        line: {
          enableMouseTracking: false,
        },
      },
      xAxis: {
        type: 'datetime' as const,
        lineWidth: 0,
        tickLength: 0,
        /**
         * We only want to show the first and last tick on the x-axis
         */
        tickPositioner(
          this: Highcharts.Axis,
        ): Highcharts.AxisTickPositionsArray {
          const { tickPositions, dataMin, dataMax } = this as ExtendedAxis;

          const firstAndLast = [
            dataMin,
            dataMax,
          ] as Highcharts.AxisTickPositionsArray;
          firstAndLast.info = tickPositions?.info;

          return firstAndLast;
        },
      },
      yAxis: {
        visible: false,
      },
    };
  }

  /**
   * Memoized data by X value.
   * This is used to quickly find the hovered point in the chart.
   */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataByXValue() {
    return this.args.trendItems.reduce<
      Record<number, TotalRevenueBreakdownTrendItem>
    >((acc, trendItem) => {
      const xValue = this.getXValueForDate(trendItem.date);
      acc[xValue] = trendItem;
      return acc;
    }, {});
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get zoneColorToUse() {
    const referenceValue = this.args.department.referenceValues.find(
      (reference) =>
        reference.referenceType === this.args.selectedReferenceType,
    );

    if (!referenceValue) {
      return ZONE_COLORS.SAME;
    }

    const isRelativeChangeNotSignificant =
      referenceValue.relativeChange &&
      Math.abs(referenceValue.relativeChange) <
        SIGNIFICANT_RELATIVE_CHANGE_THRESHOLD;

    if (
      isRelativeChangeNotSignificant ||
      referenceValue.change === null ||
      referenceValue.change === 0
    ) {
      return ZONE_COLORS.SAME;
    }

    return referenceValue.change > 0
      ? ZONE_COLORS.POSITIVE
      : ZONE_COLORS.NEGATIVE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getXValueForDate(date: Dayjs) {
    return this.hotelAndMarketService.toLocalUnixTime(date.unix());
  }

  @action setTooltipElement(element: HTMLElement): void {
    this.tooltipElement = element;
  }

  <template>
    <GeneralChartsOtaChart
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
    />

    <div {{getElement this.setTooltipElement}} hidden>
      {{#if this.hoveredPoint}}
        <MarketTotalRevenueContributionBreakdownTooltip
          @item={{this.hoveredPoint}}
          @referenceType={{@selectedReferenceType}}
        />
      {{/if}}
    </div>
  </template>
}
